import { useState, useEffect, useCallback } from 'react';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { ArrowDownRight, ArrowUpRight, ClipboardCheck, Clock3, Minus, Phone, Timer, UsersRound } from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import DateRangePicker from './DateRangePicker';
import InfoHint from './InfoHint';
import { KpiStrip, KpiTile } from './KpiStrip';
import { fetchDashboardKpi, fetchEmployeeKpi, fetchProcessingState } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import { CALL_TYPE_OPTIONS } from '../config/endoTaxonomy';
import { PANEL_IDS } from '../config/panelRegistry';
import { hasKpiStrip } from '../config/sectionFilters';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { getDefaultDateRange } from '../utils/defaultDateRange';
import {
  describeKpiComparison,
  describeMissingKpiChange,
  formatKpiChange,
  formatKpiMetric,
  resolveKpiChangeType
} from '../utils/kpiMetric';
import { describeMinutesUsage } from '../utils/minutesUsage';
import useLatestRequest from '../hooks/useLatestRequest';

const KPI_CHANGE_STYLES = {
  positive: { Icon: ArrowUpRight, className: 'text-emerald-700 dark:text-emerald-400' },
  negative: { Icon: ArrowDownRight, className: 'text-red-700 dark:text-red-400' },
  neutral: { Icon: Minus, className: 'text-muted-foreground' }
};

/** Процент рядом с числом. Слова к нему и подпись «сравнивать не с чем» видны в подсказке плитки. */
const KpiChange = ({ metric, isLoading }) => {
  const change = formatKpiChange(metric);
  if (!change) {
    return <span className="sr-only">{isLoading ? 'Загрузка…' : describeMissingKpiChange(metric)}</span>;
  }

  const { Icon, className } = KPI_CHANGE_STYLES[resolveKpiChangeType(metric)];

  return (
    <span
      data-kpi-change
      title="К прошлому периоду"
      className={`inline-flex shrink-0 items-center gap-0.5 text-[13px] font-medium ${className}`}
    >
      <Icon className="h-3.5 w-3.5 self-center" aria-hidden="true" />
      {change}
      <span className="sr-only">к прошлому периоду</span>
    </span>
  );
};

const KPI_RING_RADIUS = 9.5;

/** Кольцо лимита рядом с числом минут. При нулевом расходе дуги нет: скруглённый край дал бы точку. */
const KpiMinutesRing = ({ usage }) => (
  <svg
    role="progressbar"
    aria-label="Использовано минут из лимита"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(usage.progress)}
    viewBox="0 0 24 24"
    className="h-6 w-6 shrink-0 -rotate-90 self-center"
  >
    <title>{usage.note}</title>
    <circle
      cx="12"
      cy="12"
      r={KPI_RING_RADIUS}
      fill="none"
      strokeWidth="4"
      className={usage.isExhausted ? 'stroke-red-100 dark:stroke-red-950' : 'stroke-blue-100 dark:stroke-blue-950'}
    />
    {usage.progress > 0 && (
      <circle
        cx="12"
        cy="12"
        r={KPI_RING_RADIUS}
        fill="none"
        strokeWidth="4"
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray={`${usage.progress} 100`}
        className={usage.isExhausted ? 'stroke-red-600' : 'stroke-blue-500'}
      />
    )}
  </svg>
);

/** Плитка верхней полосы: рядом с числом процент изменения или кольцо лимита минут. */
const DashboardKpiTile = ({ card, isLoading }) => (
  <KpiTile
    panelId={card.panelId}
    title={card.title}
    hint={<InfoHint text={card.hint} label={`О показателе «${card.title}»`} trackId="period.kpi.hint" />}
    icon={card.icon}
    value={card.usage ? card.usage.value : formatKpiMetric(card.metric)}
    isLoading={isLoading}
  >
    {card.usage ? (
      <>
        {card.usage.progress !== null && <KpiMinutesRing usage={card.usage} />}
        <span className="sr-only">{card.usage.note}</span>
      </>
    ) : (
      <KpiChange metric={card.metric} isLoading={isLoading} />
    )}
  </KpiTile>
);

const Dashboard = ({
  activeSection = 'dashboard',
  dateRange,
  onDateRangeChange,
  callType = 'all',
  showCallType = true,
  onCallTypeChange
}) => {
  const isDesktop = useIsDesktop();
  const panels = usePanelSettings();
  // Где KPI видны, решает config/sectionFilters. Скрытые KPI и не запрашиваются: иначе
  // вкладка тянула бы по таймеру данные, которых никто не видит.
  const showKpi = hasKpiStrip(activeSection, isDesktop);
  const [internalDateRange, setInternalDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  const [kpi, setKpi] = useState(null);
  const [employeeKpi, setEmployeeKpi] = useState(null);
  const [processingState, setProcessingState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const kpiRequests = useLatestRequest();

  const currentDateRange = dateRange ?? internalDateRange;
  // Скрытая плитка не опрашивает сервер: её данные никто не увидит.
  const showChecklist = panels.isVisible(PANEL_IDS['ui:shared.kpi-checklist']);
  const showMinutes = panels.isVisible(PANEL_IDS['ui:shared.kpi-minutes-used']);

  const handleDateRangeChange = (range) => {
    if (onDateRangeChange) {
      onDateRangeChange(range);
      return;
    }

    setInternalDateRange(range);
  };

  // Подпись берётся из списка выбора, а не из словаря исторических кодов: сюда доходят
  // только значения, которые пользователь мог выбрать («all» отсечён строкой выше).
  const getCallTypeLabel = () => {
    if (!callType || callType === 'all') return '';
    return CALL_TYPE_OPTIONS.find((option) => option.code === callType)?.label ?? callType;
  };

  const loadKpi = useCallback(async (showSpinner = true) => {
    if (!showKpi || !currentDateRange?.from || !currentDateRange?.to) {
      return;
    }

    const request = kpiRequests.begin({ background: showSpinner === false });
    if (!request) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    setLoadError(null);

    try {
      const params = {
        date_from: format(currentDateRange.from, 'yyyy-MM-dd'),
        date_to: format(currentDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      // Плитки из других источников необязательны: их сбой оставляет прочерк в своей плитке,
      // а не гасит основные показатели.
      const [dashboard, employees, processing] = await Promise.allSettled([
        fetchDashboardKpi(params),
        showChecklist ? fetchEmployeeKpi(params) : null,
        showMinutes ? fetchProcessingState() : null
      ]);
      if (request.isCurrent()) {
        if (dashboard.status === 'fulfilled') {
          setKpi(dashboard.value);
        } else {
          setLoadError('Не удалось загрузить KPI. Попробуйте позже.');
        }
        setEmployeeKpi(employees.status === 'fulfilled' ? employees.value : null);
        setProcessingState(processing.status === 'fulfilled' ? processing.value : null);
      }
    } finally {
      const isCurrent = request.isCurrent();
      request.end();
      if (showSpinner && isCurrent) {
        setIsLoading(false);
      }
    }
  }, [callType, currentDateRange, showKpi, showChecklist, showMinutes, kpiRequests]);

  useEffect(() => {
    loadKpi();
  }, [loadKpi]);

  useEffect(() => {
    if (!showKpi) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      loadKpi(false);
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadKpi, showKpi]);

  const callTypeLabel = getCallTypeLabel();

  // Названия и подписи приходят из реестра панелей: здесь только привязка плитки к метрике.
  // Скрытые плитки отсеиваются до рендера, поэтому ключом идёт panelId, а не индекс — после
  // фильтрации индекс перестаёт быть стабильным.
  const kpiCards = [
    { panelId: PANEL_IDS['ui:shared.kpi-total-calls'], metric: kpi?.totalCalls, icon: Phone },
    { panelId: PANEL_IDS['ui:shared.kpi-avg-duration'], metric: kpi?.avgDuration, icon: Clock3 },
    { panelId: PANEL_IDS['ui:shared.kpi-checklist'], metric: employeeKpi?.avgQualityScore, icon: ClipboardCheck },
    { panelId: PANEL_IDS['ui:shared.kpi-active-employees'], metric: kpi?.activeEmployees, icon: UsersRound },
    { panelId: PANEL_IDS['ui:shared.kpi-minutes-used'], usage: describeMinutesUsage(processingState), icon: Timer }
  ]
    .filter((card) => panels.isVisible(card.panelId))
    .map((card) => ({
      ...card,
      title: panels.title(card.panelId),
      // Минуты считаются по всему периоду учёта, направление к ним не относится. Строки под числом
      // нет, поэтому сравнение словами и лимит живут здесь.
      hint: (card.usage
        ? [panels.description(card.panelId), card.usage.note]
        : [panels.description(card.panelId), callTypeLabel, describeKpiComparison(card.metric)]
      ).filter(Boolean).join(' · ')
    }));

  return (
    // `data-period-bar` — опора e2e (CallCenter.Web.E2E) для проверки классификации разделов
    // из config/sectionFilters. У самой панели нет ни заголовка, ни роли. Select
    // «Направление» — единственный комбобокс панели и единственный контрол направления
    // в приложении: показывается он только там, где раздел направление использует.
    <div data-period-bar className="flex-1 space-y-6 p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* В мобильной раскладке заголовок не показывается: название раздела уже есть
            в верхней панели, а над содержимым остаются только фильтры. */}
        <div className="max-lg:hidden">
          <h1 className="text-3xl font-bold tracking-tight">Центр аналитики</h1>
          <p className="text-muted-foreground mt-1">
            Оперативные метрики по контакт-центру
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          {showCallType && (
            <Select trackId="period.direction" trackValue value={callType} onValueChange={onCallTypeChange}>
              <SelectTrigger className="w-full sm:w-[190px]">
                <SelectValue placeholder="Направление" />
              </SelectTrigger>
              <SelectContent>
                {CALL_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* «Сбросить» возвращает к периоду, с которым открывается приложение: без периода
              разделы расходились — часть подставляла текущий месяц, часть показывала прежний. */}
          <DateRangePicker
            value={currentDateRange}
            onChange={handleDateRangeChange}
            getDefaultValue={getDefaultDateRange}
          />
        </div>
      </div>

      {showKpi && loadError && (
        <Card className="border-red-200 bg-red-50 text-red-800">
          <CardHeader>
            <CardTitle className="text-base">Ошибка загрузки</CardTitle>
            <CardDescription className="text-red-600">{loadError}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {showKpi && kpiCards.length > 0 && (
        <KpiStrip count={kpiCards.length}>
          {kpiCards.map((card) => (
            <DashboardKpiTile key={card.panelId} card={card} isLoading={isLoading} />
          ))}
        </KpiStrip>
      )}
    </div>
  );
};

export default Dashboard;
